import { observer } from "mobx-react";
import {
  IReactiveNewsContentItem,
  ReactiveContentItem,
} from "../../schoolfeed/SchoolFeedContentItem";
import React, { useRef, useState } from "react";
import styles from "./styles/NewsModal.module.scss";
import socialItemModalStyles from "../styles/SocialItemModal.module.scss";
import { UnpublishedInfoBox } from "../../utilities/UnpublishedInfoBox";
import ImageComponent from "../../utilities/ImageComponent";
import PhotoCarousel from "../../utilities/PhotoCarousel";
import { EditButton } from "../../blocks/TerracedGridTheme/EditButton";
import Moment from "moment";
import { ReadMoreButton } from "../SocialItemModal";
import {
  MeritChatThread,
  MeritChatThreadCTA,
  MeritChatThreadHeader,
} from "../../utilities/MeritChat";
import ClickableLink from "../../utilities/ClickableLink";
import {
  buildContentItemUrl,
  buildPageUrl,
} from "../../../utils/SchoolBlocksUtilities";
import { JoinDiscussionButton } from "../../schoolfeed/ContentPostDisplay/JoinDiscussionButton";
import { unpublishItem } from "../../schoolfeed/ContentPostDisplay/utils";
import NewsModalContentHeader from "./NewsModalContentHeader";
import { useStores } from "../../../stores/useStores";
import classNames from "classnames";
import { useTranslateClassName } from "../../../hooks/useTranslateClassName";
import Markdown from "../../utilities/Markdown";
import { useFeatureFlags } from "../../../utils/featureFlags/useFeatureFlags";
import ApiClient, { ApiError } from "../../../backends/ApiClient";
import NotificationManager from "../../notifications/NotificationManager";
import SchoolBlocksLoadingIndicator from "../../utilities/SchoolBlocksLoadingIndicator";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { TranslateButton } from "../../schoolfeed/ContentPostDisplay/TranslateButton";
import { getPlatform } from "../../../utils/StringUtilities";
import { sourceTypesEnum } from "../../../utils/SourceUtils";
import { Box, Text } from "@radix-ui/themes";
import { reportError } from "../../../utils/errorReporting";

export const meritChatID = "meritChatFeed";

export function UnpublishedContentItemInfoBox({ unpublishedItemType, item }) {
  return (
    <UnpublishedInfoBox
      unpublishedItemType={unpublishedItemType}
      handleClick={() => unpublishItem(item)}
    />
  );
}

const NewsModalContent = observer(
  (props: {
    index: number;
    itemObj: ReactiveContentItem;
    handleDelete: (contentItem: ContentItem) => void;
    navigationButtons?: React.ReactElement;
    closeFunction?: () => void;
    className?: string;
  }) => {
    const { show_meritocracy_chat } = useFeatureFlags();
    const { userStore, organizationStore } = useStores();
    const imageUrl = props.itemObj.image;
    const images = props.itemObj.images;
    const contentRef = useRef<HTMLDivElement>(null);
    const shareUrlPath = buildContentItemUrl(
      props.itemObj.organization_id,
      props.itemObj.id,
    );
    const { show_translate_button, show_meritchat_header } = useFeatureFlags();

    const isParentSquare =
      getPlatform(props.itemObj.type) === sourceTypesEnum.PARENTSQUARE;
    const [description, setDescription] = useState<string | null>(
      props.itemObj.item.translated ? null : props.itemObj.description,
    );
    // A post can legitimately have no description, so null alone does not mean
    // the translated description is still on its way.
    const isLoadingDescription =
      props.itemObj.item.translated && description === null;

    const containerClassName = classNames({
      [styles.container]: true,
      [props.className || ""]: props.className,
    });
    const translateClassName = useTranslateClassName();
    const getDescription = async () => {
      try {
        if (props.itemObj.item.translated) {
          const client = await ApiClient();
          const result = await client.GET(
            "/organizations/{organization_pk}/content-items/{id}/translated/",
            {
              params: {
                path: {
                  organization_pk: props.itemObj.organization_id,
                  id: props.itemObj.id,
                },
              },
            },
          );
          return result.description;
        } else {
          return props.itemObj.description;
        }
      } catch (e) {
        reportError(e, { error_source: "handled" });
        NotificationManager.error(
          e instanceof ApiError ? (e.data as any)?.detail : undefined,
        );
      }
    };

    useAsyncEffect(async () => {
      const description = await getDescription();
      setDescription(description ?? "");
    }, [props.itemObj.id, props.itemObj.description]);

    const showMeritChat =
      show_meritocracy_chat && props.itemObj.meritchat_enabled;
    const media =
      images.length > 1 ? (
        <PhotoCarousel
          key={props.itemObj.id}
          className={styles.photoCarousel}
          images={images}
          alt={props.itemObj.altText}
          showThumbnails
        />
      ) : (
        imageUrl && (
          <ImageComponent
            src={imageUrl}
            key={props.itemObj.id}
            alt={props.itemObj.altText || "Social Post Image"}
          />
        )
      );

    return (
      <div ref={contentRef} className={containerClassName}>
        {!props.itemObj.published && (
          <UnpublishedContentItemInfoBox
            unpublishedItemType={"News Item"}
            item={props.itemObj}
          />
        )}
        <NewsModalContentHeader
          index={props.index}
          closeFunction={props.closeFunction}
          itemObj={props.itemObj}
          navigationButtons={props.navigationButtons}
          shareUrlPath={shareUrlPath}
          printRef={contentRef}
        />
        {showMeritChat && show_meritchat_header && (
          <div>
            <MeritChatThreadHeader
              source_id={props.itemObj.id}
              organization_id={props.itemObj.organization_id}
              source_category={props.itemObj.type}
              language={props.itemObj.language}
            />
          </div>
        )}
        <article className={classNames({ [styles.centered]: !description })}>
          {description && media}
          <div className={styles.titleContainer}>
            {props.itemObj.title && (
              <h2 className={translateClassName}>
                <Markdown
                  withStyles={false}
                  source={props.itemObj.title}
                ></Markdown>
              </h2>
            )}
            {userStore.isEditor && (
              <EditButton
                contentItem={props.itemObj as IReactiveNewsContentItem}
                handleDelete={props.handleDelete}
              />
            )}
            {show_translate_button && (
              <TranslateButton
                itemObj={props.itemObj as IReactiveNewsContentItem}
              />
            )}
          </div>
          <div className={styles.subheader}>
            <small>
              <span>Posted on&nbsp;</span>
              <span className={styles.postDate}>
                {Moment(props.itemObj.created_at).format("MMM D, YYYY")}&nbsp;
              </span>
            </small>
            <small>
              <ClickableLink
                aria-label={`Link to ${props.itemObj.organization.title}'s News Feed`}
                title={`Link to ${props.itemObj.organization.title}'s News Feed`}
                href={buildPageUrl(props.itemObj.organization.id)}
              >{`by ${props.itemObj.organization.title}`}</ClickableLink>
            </small>
          </div>
          {!description && media}
          {showMeritChat && <MeritChatThreadCTA />}
          {isLoadingDescription ? (
            <div className={styles.loadingIndicator}>
              <SchoolBlocksLoadingIndicator />
            </div>
          ) : (
            description && (
              <div className={styles.content}>
                <Markdown
                  withStyles={true}
                  className={translateClassName}
                  source={description}
                />
              </div>
            )
          )}
          {isParentSquare && props.itemObj.link && (
            <Box
              mt="4"
              p="4"
              width={{ initial: "100%", md: "50%" }}
              style={{
                backgroundColor: "var(--accent-2)",
                border: "1px solid var(--accent-6)",
                borderRadius: "var(--radius-2)",
                textAlign: "center",
              }}
            >
              <ClickableLink
                href={props.itemObj.link}
                style={{
                  display: "inline-block",
                  padding: "12px 32px",
                  backgroundColor: "var(--accent-9)",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: 600,
                  borderRadius: "var(--radius-2)",
                  textDecoration: "none",
                }}
              >
                View full post on ParentSquare →
              </ClickableLink>
              <Text as="p" size="1" mt="2" color="gray">
                This post originates from ParentSquare. View the full content
                including any images and attachments on ParentSquare.
              </Text>
            </Box>
          )}
          {!show_meritocracy_chat &&
            organizationStore.schoolFeedEnabledForOrganization &&
            props.itemObj.meritchat_enabled && (
              <div style={{ marginBottom: 10 }}>
                <JoinDiscussionButton
                  className={socialItemModalStyles.viewBtn}
                  itemObj={props.itemObj as IReactiveNewsContentItem}
                />
              </div>
            )}
          {props.itemObj.link && !isParentSquare && (
            <ReadMoreButton itemObj={props.itemObj} />
          )}
        </article>
        {showMeritChat && (
          <div
            style={{
              clear: "left",
            }}
            id={meritChatID}
          >
            <MeritChatThread
              source_id={props.itemObj.id}
              organization_id={props.itemObj.organization_id}
              source_category={props.itemObj.type}
              language={props.itemObj.language}
            />
          </div>
        )}
      </div>
    );
  },
);

export default NewsModalContent;
